/**
 * ModuleFilesTab Component
 * 
 * Shows required and optional files for a module with upload status,
 * tooltips, and "Already uploaded" indicators.
 * 
 * For CSV files, automatically shows FieldMappingPrecheckModal after upload
 * to allow AI-assisted field mapping before analysis.
 */

import { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Upload, CheckCircle, AlertCircle, FileText, HelpCircle, RefreshCw, Loader2, Trash2, Download, Eye, ChevronDown, ChevronUp, Settings2, AlertTriangle, FileType, FileSpreadsheet, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { ModuleDefinition, FileKey, EngagementFile } from '@/lib/modules/types';
import { getFileKeyConfig, getFileKeyDisplayName } from '@/lib/modules/file-keys';
import { formatWithUTC } from '@/lib/datetime';
import { FieldMappingPrecheckModal } from '@/components/FieldMappingPrecheckModal';
import { useFieldMappings } from '@/hooks/useFieldMappings';
import { useDocumentParser } from '@/hooks/useDocumentParser';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { SieMultiFileRow } from './SieMultiFileRow';
import { GenericMultiFileRow } from './GenericMultiFileRow';

// Helper to format file size in human-readable format
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

// Helper to get human-readable file type label
function getFileTypeLabel(mimeType: string): string {
  const typeMap: Record<string, string> = {
    'text/csv': 'CSV',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
    'application/vnd.ms-excel': 'Excel',
    'application/pdf': 'PDF',
    'text/plain': 'Text',
  };
  return typeMap[mimeType] || mimeType.split('/').pop()?.toUpperCase() || 'Fil';
}

interface ModuleFilesTabProps {
  module: ModuleDefinition;
  auditId: string;
  engagementFiles: Record<FileKey, EngagementFile | undefined>;
  onFileUploaded: (fileKey: FileKey, file: EngagementFile) => void;
  onRefresh: () => void;
}

interface FileRowProps {
  fileKey: FileKey;
  required: boolean;
  uploadedFile?: EngagementFile;
  auditId: string;
  moduleId: string;
  onUploadComplete: () => void;
}

function FileRow({ fileKey, required, uploadedFile, auditId, moduleId, onUploadComplete }: FileRowProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<string[][] | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  
  // Field mapping modal state
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [pendingCsvContent, setPendingCsvContent] = useState<string>('');
  const { saveMapping, getMapping } = useFieldMappings();
  const [hasSavedMapping, setHasSavedMapping] = useState(false);
  const [parsedSourceType, setParsedSourceType] = useState<'pdf' | 'csv'>('csv');
  const [extractedRowCount, setExtractedRowCount] = useState<number | undefined>(undefined);
  
  // PDF parsing hook
  const { parseDocument, isParsing, progress: parseProgress, toCsvContent } = useDocumentParser({
    moduleType: moduleId,
  });
  
  const config = getFileKeyConfig(fileKey);
  const displayName = getFileKeyDisplayName(fileKey);

  const isCsvFile = uploadedFile?.mimeType === 'text/csv' || 
    uploadedFile?.originalFileName.toLowerCase().endsWith('.csv');
  
  const isPdfFile = uploadedFile?.mimeType === 'application/pdf' ||
    uploadedFile?.originalFileName.toLowerCase().endsWith('.pdf');

  const isExcelFile = uploadedFile?.mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    uploadedFile?.mimeType === 'application/vnd.ms-excel' ||
    uploadedFile?.originalFileName.toLowerCase().endsWith('.xlsx') ||
    uploadedFile?.originalFileName.toLowerCase().endsWith('.xls');

  // Check if file is parseable (CSV, PDF, or Excel)
  const isParseableFile = isCsvFile || isPdfFile || isExcelFile;
  
  // Check if mapping exists for this file when component mounts or file changes
  const checkExistingMapping = useCallback(async () => {
    if (!uploadedFile || !isParseableFile) return;
    try {
      const mapping = await getMapping(auditId, moduleId);
      setHasSavedMapping(!!mapping);
    } catch {
      setHasSavedMapping(false);
    }
  }, [uploadedFile, isParseableFile, auditId, moduleId, getMapping]);
  
  // Check mapping on mount and when file changes
  useEffect(() => {
    checkExistingMapping();
  }, [checkExistingMapping]);

  const loadPreview = useCallback(async () => {
    if (!uploadedFile || previewData) return;
    
    try {
      setIsLoadingPreview(true);
      const { data, error } = await supabase.storage
        .from('audit-documents')
        .download(uploadedFile.storagePath);

      if (error) throw error;

      const text = await data.text();
      const lines = text.split('\n').slice(0, 6); // Header + 5 rows
      const parsed = lines
        .filter(line => line.trim())
        .map(line => {
          // Simple CSV parsing (handles basic cases)
          const result: string[] = [];
          let current = '';
          let inQuotes = false;
          
          for (const char of line) {
            if (char === '"') {
              inQuotes = !inQuotes;
            } else if ((char === ',' || char === ';') && !inQuotes) {
              result.push(current.trim());
              current = '';
            } else {
              current += char;
            }
          }
          result.push(current.trim());
          return result;
        });
      
      setPreviewData(parsed);
    } catch (error) {
      console.error('Preview error:', error);
      toast({ 
        title: 'Kunde inte ladda förhandsgranskning', 
        variant: 'destructive' 
      });
    } finally {
      setIsLoadingPreview(false);
    }
  }, [uploadedFile, previewData, toast]);

  const togglePreview = useCallback(() => {
    if (!showPreview && !previewData) {
      loadPreview();
    }
    setShowPreview(prev => !prev);
  }, [showPreview, previewData, loadPreview]);

  const handleDelete = useCallback(async () => {
    if (!uploadedFile) return;
    
    try {
      setIsDeleting(true);
      
      // Get current user for soft delete tracking
      const { data: { user } } = await supabase.auth.getUser();
      
      // Soft delete: Mark as deleted instead of removing
      // File stays in storage and can be recovered for 30 days
      const { error: dbError } = await supabase
        .from('audit_documents')
        .update({ 
          deleted_at: new Date().toISOString(),
          deleted_by: user?.id || null
        })
        .eq('id', uploadedFile.id);

      if (dbError) throw dbError;

      toast({ 
        title: 'Fil raderad', 
        description: `${uploadedFile.originalFileName} kan återställas i 30 dagar`
      });
      onUploadComplete();
    } catch (error: unknown) {
      console.error('Delete error:', error);
      toast({ 
        title: 'Radering misslyckades', 
        description: error instanceof Error ? error.message : 'Ett fel uppstod',
        variant: 'destructive' 
      });
    } finally {
      setIsDeleting(false);
    }
  }, [uploadedFile, toast, onUploadComplete]);

  const handleDownload = useCallback(async () => {
    if (!uploadedFile) return;
    
    try {
      const { data, error } = await supabase.storage
        .from('audit-documents')
        .download(uploadedFile.storagePath);

      if (error) throw error;

      // Create download link
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = uploadedFile.originalFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error: unknown) {
      console.error('Download error:', error);
      toast({ 
        title: 'Nedladdning misslyckades', 
        description: error instanceof Error ? error.message : 'Ett fel uppstod',
        variant: 'destructive' 
      });
    }
  }, [uploadedFile, toast]);

  // Handler to open field mapping modal for existing parseable files (CSV, PDF, or Excel)
  const openMappingModal = useCallback(async () => {
    if (!uploadedFile || !isParseableFile) return;
    
    try {
      const { data, error } = await supabase.storage
        .from('audit-documents')
        .download(uploadedFile.storagePath);

      if (error) throw error;

      if (isCsvFile) {
        // For CSV, just read the text
        const text = await data.text();
        setPendingCsvContent(text);
        setParsedSourceType('csv');
        setExtractedRowCount(undefined);
        setShowMappingModal(true);
      } else if (isPdfFile) {
        // For PDF, we need to parse it first
        const file = new File([data], uploadedFile.originalFileName, { type: 'application/pdf' });
        
        toast({
          title: 'Tolkar PDF...',
          description: 'AI extraherar tabelldata, detta kan ta 10-30 sekunder',
        });
        
        const parsed = await parseDocument(file, moduleId);
        
        if (parsed && parsed.rows.length > 0) {
          const csvContent = toCsvContent(parsed);
          setPendingCsvContent(csvContent);
          setParsedSourceType('pdf');
          setExtractedRowCount(parsed.rows.length);
          setShowMappingModal(true);
          
          toast({
            title: 'PDF tolkad',
            description: `${parsed.rows.length} rader extraherades`,
          });
        } else {
          toast({
            title: 'PDF-tolkning misslyckades',
            description: 'Kunde inte extrahera tabelldata från PDF:en.',
            variant: 'destructive',
          });
        }
      } else if (isExcelFile) {
        // For Excel, parse with Azure Document Intelligence
        const file = new File([data], uploadedFile.originalFileName, { 
          type: uploadedFile.mimeType 
        });
        
        toast({
          title: 'Tolkar Excel-fil...',
          description: 'Extraherar tabelldata',
        });
        
        const parsed = await parseDocument(file, moduleId);
        
        if (parsed && parsed.rows.length > 0) {
          const csvContent = toCsvContent(parsed);
          setPendingCsvContent(csvContent);
          setParsedSourceType('csv'); // Treat as CSV after parsing
          setExtractedRowCount(parsed.rows.length);
          setShowMappingModal(true);
          
          toast({
            title: 'Excel-fil tolkad',
            description: `${parsed.rows.length} rader extraherades`,
          });
        } else {
          toast({
            title: 'Excel-tolkning misslyckades',
            description: 'Kunde inte extrahera tabelldata från Excel-filen.',
            variant: 'destructive',
          });
        }
      }
    } catch (error) {
      console.error('Failed to load file for mapping:', error);
      toast({ 
        title: 'Kunde inte ladda fil', 
        variant: 'destructive' 
      });
    }
  }, [uploadedFile, isParseableFile, isCsvFile, isPdfFile, isExcelFile, moduleId, parseDocument, toCsvContent, toast]);
  
  // Handler when field mapping is confirmed
  const handleMappingConfirm = useCallback(async (
    mapping: Record<string, string>, 
    dialect: { delimiter: string; hasHeader: boolean }
  ) => {
    try {
      await saveMapping(
        auditId, // Use auditId as customerId for audit-specific mappings
        moduleId,
        mapping,
        { delimiter: dialect.delimiter, encoding: 'utf-8', hasHeader: dialect.hasHeader }
      );
      
      setShowMappingModal(false);
      setPendingCsvContent('');
      setHasSavedMapping(true);
      
      toast({
        title: 'Fältmappning sparad',
        description: 'Mappningen kommer användas vid analys',
      });
      
      onUploadComplete();
    } catch (error) {
      console.error('Failed to save mapping:', error);
      toast({
        title: 'Kunde inte spara mappning',
        variant: 'destructive',
      });
    }
  }, [auditId, moduleId, saveMapping, toast, onUploadComplete]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];

    try {
      setIsUploading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Inte inloggad');

      // Sanitize filename for Supabase Storage (remove invalid characters)
      const originalFileName = file.name;
      const sanitizedFileName = originalFileName
        .normalize('NFD')               // Decompose accented chars (ö → o + combining ¨)
        .replace(/[\u0300-\u036f]/g, '') // Remove combining diacritical marks
        .replace(/[:\/<>|"\\?*]/g, '_') // Replace invalid chars with underscore
        .replace(/\s+/g, '_')           // Replace spaces with underscore
        .replace(/[^\w.-]/g, '_')       // Replace any remaining non-word chars
        .replace(/_+/g, '_')            // Collapse multiple underscores
        .replace(/^_|_$/g, '');         // Trim leading/trailing underscores
      
      const fileNameWasChanged = sanitizedFileName !== originalFileName;
      
      // Check file types - we'll show mapping modal for CSV, PDF, and Excel
      const isNewCsvFile = file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');
      const isNewPdfFile = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      const isNewExcelFile = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel' ||
        file.name.toLowerCase().endsWith('.xlsx') ||
        file.name.toLowerCase().endsWith('.xls');

      // Delete any existing files with the same file_key to prevent duplicates
      const { data: existingDocs } = await supabase
        .from('audit_documents')
        .select('id, storage_path')
        .eq('audit_id', auditId)
        .eq('file_key', fileKey);

      if (existingDocs && existingDocs.length > 0) {
        // Delete from storage
        const storagePaths = existingDocs.map(doc => doc.storage_path);
        await supabase.storage
          .from('audit-documents')
          .remove(storagePaths);
        
        // Delete from database
        await supabase
          .from('audit_documents')
          .delete()
          .eq('audit_id', auditId)
          .eq('file_key', fileKey);
      }

      // Upload to storage
      const filePath = `${auditId}/${moduleId}/${fileKey}/${Date.now()}_${sanitizedFileName}`;
      const { error: uploadError } = await supabase.storage
        .from('audit-documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Save document record (keep original filename for display, sanitized for storage)
      const { error: dbError } = await supabase
        .from('audit_documents')
        .insert({
          audit_id: auditId,
          filename: sanitizedFileName,
          original_filename: originalFileName,
          mime_type: file.type,
          size_bytes: file.size,
          storage_path: filePath,
          uploaded_by: user.id,
          module_type: moduleId,
          file_key: fileKey,
        });

      if (dbError) throw dbError;

      // Show feedback with info about renamed file if applicable
      if (fileNameWasChanged) {
        toast({ 
          title: 'Fil uppladdad', 
          description: `"${originalFileName}" sparades som "${sanitizedFileName}" (ogiltiga tecken ersattes)`,
        });
      } else {
        toast({ title: 'Fil uppladdad', description: originalFileName });
      }
      setIsReplacing(false);
      
      // For CSV files, show field mapping modal automatically
      if (isNewCsvFile) {
        const csvContent = await file.text();
        setPendingCsvContent(csvContent);
        setParsedSourceType('csv');
        setExtractedRowCount(undefined);
        setShowMappingModal(true);
      } 
      // For PDF files, parse with AI and convert to CSV for field mapping
      else if (isNewPdfFile) {
        // Show recommendation for CSV
        toast({
          title: '💡 Tips: CSV rekommenderas',
          description: 'För bäst resultat, exportera skattekontot som CSV direkt från Skatteverket. PDF-tolkning kan i sällsynta fall missa rader.',
          duration: 10000,
        });
        
        toast({
          title: 'Tolkar PDF...',
          description: 'AI extraherar tabelldata, detta kan ta 10-30 sekunder',
        });
        
        const parsed = await parseDocument(file, moduleId);
        
        if (parsed && parsed.rows.length > 0) {
          // Convert parsed data to CSV format
          const csvContent = toCsvContent(parsed);
          setPendingCsvContent(csvContent);
          setParsedSourceType('pdf');
          setExtractedRowCount(parsed.rows.length);
          setShowMappingModal(true);
          
          toast({
            title: 'PDF tolkad',
            description: `${parsed.rows.length} rader extraherades (${Math.round(parsed.overallConfidence * 100)}% konfidens)`,
          });
        } else {
          toast({
            title: 'PDF-tolkning misslyckades',
            description: 'Kunde inte extrahera tabelldata från PDF:en. Prova att konvertera till CSV manuellt.',
            variant: 'destructive',
          });
        }
      }
      // For Excel files, parse with Azure Document Intelligence and convert to CSV
      else if (isNewExcelFile) {
        toast({
          title: 'Tolkar Excel-fil...',
          description: 'Extraherar tabelldata, detta kan ta några sekunder',
        });
        
        const parsed = await parseDocument(file, moduleId);
        
        if (parsed && parsed.rows.length > 0) {
          const csvContent = toCsvContent(parsed);
          setPendingCsvContent(csvContent);
          setParsedSourceType('csv'); // Treat as CSV after parsing
          setExtractedRowCount(parsed.rows.length);
          setShowMappingModal(true);
          
          toast({
            title: 'Excel-fil tolkad',
            description: `${parsed.rows.length} rader extraherades`,
          });
        } else {
          toast({
            title: 'Excel-tolkning misslyckades',
            description: 'Kunde inte extrahera tabelldata från Excel-filen. Prova att spara som CSV.',
            variant: 'destructive',
          });
        }
      }
      
      onUploadComplete();
    } catch (error: unknown) {
      console.error('Upload error:', error);
      
      // Provide descriptive error messages
      let errorMessage = 'Ett okänt fel uppstod';
      
      if (error && typeof error === 'object' && 'message' in error) {
        const msg = (error as { message: string }).message;
        
        if (msg.includes('Invalid key')) {
          errorMessage = 'Filnamnet innehåller ogiltiga tecken. Prova att byta namn på filen.';
        } else if (msg.includes('Payload too large') || msg.includes('file size')) {
          errorMessage = 'Filen är för stor. Max 50 MB tillåtet.';
        } else if (msg.includes('mime type') || msg.includes('not allowed')) {
          errorMessage = 'Filtypen stöds inte. Använd CSV, Excel, PDF eller SIE-format.';
        } else if (msg.includes('not authenticated') || msg.includes('JWT')) {
          errorMessage = 'Din session har gått ut. Ladda om sidan och logga in igen.';
        } else if (msg.includes('permission') || msg.includes('policy')) {
          errorMessage = 'Du har inte behörighet att ladda upp filer till detta ärende.';
        } else if (msg.includes('duplicate') || msg.includes('already exists')) {
          errorMessage = 'En fil med samma namn finns redan. Prova ett annat filnamn.';
        } else if (msg.includes('network') || msg.includes('fetch')) {
          errorMessage = 'Nätverksfel. Kontrollera din internetanslutning och försök igen.';
        } else {
          errorMessage = msg;
        }
      }
      
      toast({ 
        title: 'Uppladdning misslyckades', 
        description: errorMessage,
        variant: 'destructive' 
      });
    } finally {
      setIsUploading(false);
    }
  }, [auditId, moduleId, fileKey, toast, onUploadComplete]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt', '.se', '.sie'],
    },
    maxFiles: 1,
    disabled: isUploading,
  });

  const showUploader = !uploadedFile || isReplacing;

  return (
    <div className="flex items-center justify-between p-4 border rounded-lg bg-card">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className={`p-2 rounded-lg ${uploadedFile && !isReplacing ? 'bg-success/10' : 'bg-muted'}`}>
          {uploadedFile && !isReplacing ? (
            <CheckCircle className="h-5 w-5 text-success" />
          ) : (
            <FileText className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm truncate">{displayName}</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help flex-shrink-0" />
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-sm">
                  <p className="text-sm">{t(config.descriptionKey, displayName)}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Format: {config.formats.join(', ')}
                  </p>
                  {config.expectedFields && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Fält: {config.expectedFields.join(', ')}
                    </p>
                  )}
                  {config.exampleData && (
                    <p className="text-xs font-mono mt-1 bg-muted/50 px-1 rounded">
                      Ex: {config.exampleData}
                    </p>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Badge variant={required ? 'default' : 'secondary'} className="text-xs flex-shrink-0">
              {required ? 'Obligatorisk' : 'Valfri'}
            </Badge>
          </div>
          
          {uploadedFile && !isReplacing ? (
            <div className="text-xs text-muted-foreground mt-1 flex flex-wrap items-center gap-x-1">
              <span className="font-medium text-foreground">{uploadedFile.originalFileName}</span>
              <span>•</span>
              <span>{formatFileSize(uploadedFile.sizeBytes)}</span>
              <span>•</span>
              <span>{getFileTypeLabel(uploadedFile.mimeType)}</span>
              <span>•</span>
              <span>Uppladdad {formatWithUTC(uploadedFile.uploadedAt)}</span>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
              <p>
                <span className="text-foreground">Exempel:</span> {config.exampleFileName}
              </p>
              {config.expectedFields && (
                <p>
                  <span className="text-foreground">Förväntade fält:</span>{' '}
                  {config.expectedFields.join(', ')}
                </p>
              )}
              {config.exampleData && (
                <p>
                  <span className="text-foreground">Dataexempel:</span>{' '}
                  <span className="font-mono text-[10px] bg-muted px-1.5 py-0.5 rounded">
                    {config.exampleData}
                  </span>
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 ml-4">
        {uploadedFile && !isReplacing ? (
          <>
            {/* Field mapping button - show for both CSV and PDF */}
            {isParseableFile && (
              <>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant={hasSavedMapping ? "ghost" : "outline"}
                        size="sm"
                        onClick={openMappingModal}
                        disabled={isParsing}
                        className={hasSavedMapping ? "" : "border-primary text-primary hover:bg-primary/10"}
                      >
                        {isParsing ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Settings2 className="h-3 w-3" />
                        )}
                        {!hasSavedMapping && <span className="ml-1 text-xs">Mappa fält</span>}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {hasSavedMapping ? 'Ändra fältmappning' : 'Konfigurera fältmappning för analys'}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                
                {/* Preview button - only for CSV */}
                {isCsvFile && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={togglePreview}
                          disabled={isLoadingPreview}
                        >
                          {isLoadingPreview ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : showPreview ? (
                            <ChevronUp className="h-3 w-3" />
                          ) : (
                            <Eye className="h-3 w-3" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{showPreview ? 'Dölj förhandsgranskning' : 'Visa förhandsgranskning'}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </>
            )}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={handleDownload}
                  >
                    <Download className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Ladda ner</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setIsReplacing(true)}
              className="gap-1"
            >
              <RefreshCw className="h-3 w-3" />
              Ersätt
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Trash2 className="h-3 w-3" />
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Radera fil?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Är du säker på att du vill radera "{uploadedFile.originalFileName}"? 
                    Detta kan inte ångras.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Avbryt</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleDelete}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Radera
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        ) : (
          <div 
            {...getRootProps()} 
            className={`border-2 border-dashed rounded-lg px-4 py-2 cursor-pointer transition-colors
              ${isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'}
              ${isUploading ? 'opacity-50 cursor-wait' : ''}`}
          >
            <input {...getInputProps()} />
            {isUploading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Laddar upp...
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Upload className="h-4 w-4" />
                {isDragActive ? 'Släpp här' : 'Ladda upp'}
              </div>
            )}
          </div>
        )}
        {isReplacing && (
          <Button variant="ghost" size="sm" onClick={() => setIsReplacing(false)}>
            Avbryt
          </Button>
        )}
      </div>

      {/* CSV Preview Section */}
      {uploadedFile && isCsvFile && showPreview && (
        <div className="mt-3 border-t pt-3">
          {isLoadingPreview ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Laddar förhandsgranskning...
            </div>
          ) : previewData && previewData.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-muted/50">
                    {previewData[0].map((header, i) => (
                      <th key={i} className="px-2 py-1.5 text-left font-medium border-b truncate max-w-32">
                        {header || `Kolumn ${i + 1}`}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.slice(1).map((row, rowIndex) => (
                    <tr key={rowIndex} className="hover:bg-muted/30">
                      {row.map((cell, cellIndex) => (
                        <td key={cellIndex} className="px-2 py-1 border-b border-muted truncate max-w-32" title={cell}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-muted-foreground mt-2">
                Visar första 5 raderna av filen
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Ingen data att visa</p>
          )}
        </div>
      )}
      
      {/* Field Mapping Modal */}
      <FieldMappingPrecheckModal
        isOpen={showMappingModal}
        onClose={() => {
          setShowMappingModal(false);
          setPendingCsvContent('');
          setExtractedRowCount(undefined);
        }}
        onConfirm={handleMappingConfirm}
        csvContent={pendingCsvContent}
        customerId={auditId}
        moduleName={moduleId}
        sourceType={parsedSourceType}
        extractedRowCount={extractedRowCount}
      />
      
      {/* PDF Parsing Loading Dialog */}
      <Dialog open={isParsing}>
        <DialogContent className="text-center sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <div className="flex flex-col items-center gap-4 py-4">
            <FileSpreadsheet className="h-12 w-12 text-primary animate-pulse" />
            <DialogTitle>Tolkar PDF...</DialogTitle>
            <DialogDescription>
              AI extraherar tabelldata från dokumentet. Detta kan ta 10-30 sekunder beroende på filstorlek.
            </DialogDescription>
            <Progress value={parseProgress} className="w-full" />
            <p className="text-xs text-muted-foreground">
              {parseProgress < 40 ? 'Förbereder dokument...' : 
               parseProgress < 80 ? 'Extraherar tabelldata...' : 
               'Slutför bearbetning...'}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ModuleFilesTab({ 
  module, 
  auditId, 
  engagementFiles, 
  onFileUploaded,
  onRefresh,
}: ModuleFilesTabProps) {
  const { t } = useTranslation();

  const requiredCount = module.requiredFiles.length;
  const requiredUploaded = module.requiredFiles.filter(fk => engagementFiles[fk]).length;
  const recommendedCount = module.recommendedFiles?.length || 0;
  const recommendedUploaded = module.recommendedFiles?.filter(fk => engagementFiles[fk]).length || 0;
  const optionalCount = module.optionalFiles.length;
  const optionalUploaded = module.optionalFiles.filter(fk => engagementFiles[fk]).length;

  const allRequiredUploaded = requiredUploaded === requiredCount;

  return (
    <div className="space-y-6">
      {/* CSV Recommendation Info */}
      <Alert className="bg-muted/50 border-muted">
        <Info className="h-4 w-4" />
        <AlertDescription>
          <strong>Rekommenderat:</strong> Ladda upp CSV-export från Skatteverket för bäst resultat. 
          PDF-filer tolkas med AI och kan i sällsynta fall missa rader.
        </AlertDescription>
      </Alert>

      {/* Summary Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Filstatus
              </CardTitle>
              <CardDescription>
                Ladda upp de filer som krävs för att köra analysen
              </CardDescription>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                {allRequiredUploaded ? (
                  <CheckCircle className="h-4 w-4 text-success" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-warning" />
                )}
                <span>Obligatoriska: {requiredUploaded}/{requiredCount}</span>
              </div>
              {recommendedCount > 0 && (
                <div className="flex items-center gap-2">
                  {recommendedUploaded === recommendedCount ? (
                    <CheckCircle className="h-4 w-4 text-success" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-warning" />
                  )}
                  <span className="text-warning">Rekommenderade: {recommendedUploaded}/{recommendedCount}</span>
                </div>
              )}
              {optionalCount > 0 && (
                <div className="text-muted-foreground">
                  Valfria: {optionalUploaded}/{optionalCount}
                </div>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Required Files */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-primary" />
          Obligatoriska filer
        </h3>
        <div className="space-y-2">
          {module.requiredFiles.map((fileKey) => {
            const fileConfig = getFileKeyConfig(fileKey);
            
            if (fileKey === 'GL_SIE') {
              return (
                <div key={fileKey} className="space-y-4">
                  <SieMultiFileRow
                    fileKey={fileKey}
                    required={true}
                    auditId={auditId}
                    moduleId={module.id}
                    onUploadComplete={onRefresh}
                    label="SIE-fil (innevarande år)"
                    fiscalYearIndex={0}
                  />
                  <SieMultiFileRow
                    fileKey={fileKey}
                    required={false}
                    auditId={auditId}
                    moduleId={module.id}
                    onUploadComplete={onRefresh}
                    label="SIE-fil (föregående år)"
                    fiscalYearIndex={-1}
                  />
                </div>
              );
            }
            
            // Use GenericMultiFileRow for file keys that support multiple uploads
            if (fileConfig?.allowMultiple) {
              return (
                <GenericMultiFileRow
                  key={fileKey}
                  fileKey={fileKey}
                  required={true}
                  auditId={auditId}
                  moduleId={module.id}
                  onUploadComplete={onRefresh}
                />
              );
            }
            
            return (
              <FileRow
                key={fileKey}
                fileKey={fileKey}
                required={true}
                uploadedFile={engagementFiles[fileKey]}
                auditId={auditId}
                moduleId={module.id}
                onUploadComplete={onRefresh}
              />
            );
          })}
        </div>
      </div>

      {/* Recommended Files */}
      {module.recommendedFiles && module.recommendedFiles.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-warning flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            {module.id === 'data_quality' 
              ? 'Rekommenderade filer (obligatoriska i övriga moduler)' 
              : 'Rekommenderade filer (utökad analys)'}
          </h3>
          <div className="space-y-2">
            {module.recommendedFiles.map((fileKey) => {
              const fileConfig = getFileKeyConfig(fileKey);
              
              if (fileKey === 'GL_SIE') {
                return (
                  <div key={fileKey} className="space-y-4">
                    <SieMultiFileRow
                      fileKey={fileKey}
                      required={false}
                      auditId={auditId}
                      moduleId={module.id}
                      onUploadComplete={onRefresh}
                      label="SIE-fil (innevarande år)"
                      fiscalYearIndex={0}
                    />
                    <SieMultiFileRow
                      fileKey={fileKey}
                      required={false}
                      auditId={auditId}
                      moduleId={module.id}
                      onUploadComplete={onRefresh}
                      label="SIE-fil (föregående år)"
                      fiscalYearIndex={-1}
                    />
                  </div>
                );
              }
              
              if (fileConfig?.allowMultiple) {
                return (
                  <GenericMultiFileRow
                    key={fileKey}
                    fileKey={fileKey}
                    required={false}
                    auditId={auditId}
                    moduleId={module.id}
                    onUploadComplete={onRefresh}
                  />
                );
              }
              
              return (
                <FileRow
                  key={fileKey}
                  fileKey={fileKey}
                  required={false}
                  uploadedFile={engagementFiles[fileKey]}
                  auditId={auditId}
                  moduleId={module.id}
                  onUploadComplete={onRefresh}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Optional Files */}
      {module.optionalFiles.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-muted-foreground" />
            Valfria filer (förbättrar analysen)
          </h3>
          <div className="space-y-2">
            {module.optionalFiles.map((fileKey) => {
              const fileConfig = getFileKeyConfig(fileKey);
              
              if (fileKey === 'GL_SIE') {
                return (
                  <div key={fileKey} className="space-y-4">
                    <SieMultiFileRow
                      fileKey={fileKey}
                      required={false}
                      auditId={auditId}
                      moduleId={module.id}
                      onUploadComplete={onRefresh}
                      label="SIE-fil (innevarande år)"
                      fiscalYearIndex={0}
                    />
                    <SieMultiFileRow
                      fileKey={fileKey}
                      required={false}
                      auditId={auditId}
                      moduleId={module.id}
                      onUploadComplete={onRefresh}
                      label="SIE-fil (föregående år)"
                      fiscalYearIndex={-1}
                    />
                  </div>
                );
              }
              
              if (fileConfig?.allowMultiple) {
                return (
                  <GenericMultiFileRow
                    key={fileKey}
                    fileKey={fileKey}
                    required={false}
                    auditId={auditId}
                    moduleId={module.id}
                    onUploadComplete={onRefresh}
                  />
                );
              }
              
              return (
                <FileRow
                  key={fileKey}
                  fileKey={fileKey}
                  required={false}
                  uploadedFile={engagementFiles[fileKey]}
                  auditId={auditId}
                  moduleId={module.id}
                  onUploadComplete={onRefresh}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
